import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import { CustomerNavbar, BottomNavigation, customerBottomNavItems } from '../navigation';
import { PageTransition } from '../PageTransition';
import { useAuth } from '../../contexts/AuthContext';
import { useLogout } from '../../hooks/auth';
import { useIsMobile } from '../../hooks/useIsMobile';

interface CustomerLayoutProps {
  children: ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | false;
}

const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  flexDirection: 'column',
}));

const ContentWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasBottomNav',
})<{ hasBottomNav?: boolean }>(({ theme, hasBottomNav }) => ({
  flex: 1,
  paddingTop: theme.spacing(3),
  paddingBottom: hasBottomNav
    ? 'calc(56px + env(safe-area-inset-bottom) + 16px)'
    : theme.spacing(4),
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(8),
  },
}));

export function CustomerLayout({ children, maxWidth = 'lg' }: CustomerLayoutProps) {
  const { user } = useAuth();
  const { mutate: logout, isPending: isLoggingOut } = useLogout();
  const isMobile = useIsMobile();
  const location = useLocation();

  // If no user, don't render layout (shouldn't happen in protected routes)
  if (!user) {
    return <>{children}</>;
  }

  return (
    <PageWrapper>
      {/* Customer-specific minimal navbar */}
      <CustomerNavbar user={user} onLogout={() => logout()} isLoggingOut={isLoggingOut} />

      {/* Full-width content (no sidebar) */}
      <ContentWrapper hasBottomNav={isMobile}>
        <Container maxWidth={maxWidth} sx={{ px: { xs: 2, sm: 3 } }}>
          <PageTransition pageKey={location.pathname}>{children}</PageTransition>
        </Container>
      </ContentWrapper>

      {/* Mobile bottom navigation */}
      {isMobile && <BottomNavigation items={customerBottomNavItems} />}
    </PageWrapper>
  );
}
